import { Box, Flex, HStack, Text, VStack } from "@chakra-ui/react";
import GCPImage from "components/GCPImage";
import { addHours, format, parseISO } from "date-fns";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import appStore from "store";
import api from "store/api";
import { sendAppierEvent } from "lib/appier";

function Success() {
  const { t } = useTranslation("common");
  const { member } = appStore;
  const router = useRouter();
  const [orderData, setOrderData] = useState({});
  const [idStatus, setIdStatus] = useState({});
  const [isVerifyComplete, setIsVerifyComplete] = useState(false);

  useEffect(() => {
    if (!member?.id) return;
    Promise.all([
      api.getMyOrder(router.query.order_id),
      api.getMyIdStatus(),
    ]).then((resps) => {
      // console.log(resps[0].data);
      setOrderData(resps[0].data);
      setIdStatus(resps[1].data);

      if (resps[1].data) {
        if (resps[0].data.car.service_location.provider !== "UDRIVE") {
          setIsVerifyComplete(true);
        } else {
          if (
            resps[1].data.driver_license_status === "success" &&
            resps[1].data.id_card_status === "success"
          ) {
            if (resps[1].data.country === "TW") {
              setIsVerifyComplete(true);
            } else {
              if (
                resps[1].data.billing_address !== "" &&
                resps[1].data.billing_address !== null
              ) {
                setIsVerifyComplete(true);
              }
            }
          }
        }
      }
    });
  }, [router.query.order_id, member?.id]);

  useEffect(() => {
    if (orderData?.amount == null) return;
    // 用 LocalStorage 紀錄已追蹤的訂單 ID，避免重複送出
    const trackedUDOrders = JSON.parse(
      localStorage.getItem("trackedUDOrders") || "[]"
    );
    if (trackedUDOrders.includes(orderData.id)) return;
    sendAppierEvent("order_completed", {
      order_id: orderData.id,
      user_id: member.id,
      phoneNo: member.phone,
      user_name: member.fullname,
      email: member.email,
      service_name: "租車",
      service_id: "UD_TW",
      amount: orderData.amount,
      payment_method:
        orderData.payments[0]?.payment_method || member.payment_method,
      created_at: new Date(orderData.created_at).getTime(),
      // coupon: orderData.coupon, // 不確定型態是什麼，所以先不送
      url: window.location.href,
    });

    if (typeof window !== "undefined" && window.gtag) {
      gtag("event", "conversion", {
        send_to: "AW-11474530947/gOn5CJSakokZEIPlvd8q",
        value: orderData.amount,
        currency: "TWD",
        transaction_id: orderData.id || "default_transaction_id",
      });
    }

    if (typeof window !== "undefined" && window.taq) {
      window.taq("track", "CZq5iOR-neL2", "Purchase");
    }

    // 將訂單 ID 加入已追蹤列表並保存
    trackedUDOrders.push(orderData.id);
    localStorage.setItem("trackedUDOrders", JSON.stringify(trackedUDOrders));
  }, [orderData, orderData?.amount, orderData?.transactionId]);

  const createCalendar = () => {
    const title = `UDRIVE Tesla ${t(
      "BookSuccessfully_title_googleCalendar"
    )}｜${orderData.car.model}`;
    const startTime = new Date(parseISO(orderData.start_time));
    const endTime = new Date(parseISO(orderData.end_time));
    const startStr =
      format(startTime, "yyyyMMdd_HHmmss").replace("_", "T") + ".000ZZ";
    const endStr =
      format(endTime, "yyyyMMdd_HHmmss").replace("_", "T") + ".000ZZ";

    const detail = `${t("pickupTime")}：${format(
      addHours(startTime, 8),
      "yyyy/MM/dd HH:mm"
    )} | ${t("pickupAddress")}：${
      orderData.car_pickup_data.location.city +
      orderData.car_pickup_data.location.zone +
      orderData.car_pickup_data.location.road
    } | ${t("returnTime")}：${format(
      addHours(endTime, 8),
      "yyyy/MM/dd HH:mm"
    )} | ${t("returnAddress")}：${
      orderData.car_return_data.location.city +
      orderData.car_return_data.location.zone +
      orderData.car_return_data.location.road
    } | ${t(
      "BookSuccessfully_btn_order"
    )}：https://www.udrive.city/account/rental_orders | ${t(
      "BookSuccessfully_btn_customerService"
    )}：https://udrive.city/?uchat`;

    window.open(
      `https://calendar.google.com/calendar/event?action=TEMPLATE&text=${title}&dates=${startStr}/${endStr}&details=${detail}`,
      "_blank"
    );
  };

  return (
    <Flex
      minH="100vh"
      bg="url('/images/home/modelx.jpg')"
      bgRepeat="no-repeat"
      bgPosition="center"
      bgSize="cover"
    >
      <Flex
        // bg="rgba(255, 255, 255, 0.6)"
        w="100%"
        style={{ backdropFilter: " blur(18px)" }}
        align="start"
        justify="center"
      >
        <VStack bg="white" p="16px" maxW="650px" borderRadius="5px">
          <Box mb={isVerifyComplete ? "56px" : "32px"}>
            <Box pos="relative">
              <Box
                pos="absolute"
                bottom="3px"
                left="0px"
                w={isVerifyComplete ? "329px" : "236px"}
                h="12px"
                bg="rgba(10, 122, 255, 0.25)"
              ></Box>
              <Text
                fontSize="24px"
                lineHeight="32px"
                color="#333333"
                fontWeight="700"
              >
                {orderData?.car?.service_location?.provider === "UDRIVE"
                  ? t("Success_title")
                  : orderData?.car?.service_location?.order_auto_cancel_type ===
                      "minute"
                    ? t("Success_provider_pending")
                    : t("Success_title")}
                <br />
                {orderData?.car?.service_location?.provider === "UDRIVE"
                  ? isVerifyComplete
                    ? t("Success_no_need_verify")
                    : t("Success_need_verify")
                  : orderData?.car?.service_location?.order_auto_cancel_type ===
                      "minute"
                    ? t("Success_provider_pending_hour", {
                        hour:
                          orderData?.car?.service_location
                            ?.order_auto_cancel_minutes / 60,
                      })
                    : t("Success_provider_acceptance")}
              </Text>
            </Box>
            <Text fontSize="16px" lineHeight="24px" color="#748598" mt="2px">
              {orderData?.car?.service_location?.provider === "UDRIVE"
                ? isVerifyComplete
                  ? t("Success_no_need_verify_info")
                  : t("Success_need_verify_info")
                : t("Success_provider_info")}
            </Text>
            <Flex
              w="100%"
              justify="center"
              mt={isVerifyComplete ? "43px" : "14px"}
            >
              <GCPImage
                w={{
                  base: isVerifyComplete ? "190px" : "272px",
                  md: isVerifyComplete ? "300px" : "400px",
                }}
                src={`/images/order/success/${
                  isVerifyComplete ? "no_need_verify.png" : "need_verify.png"
                }`}
              />
            </Flex>
          </Box>
          <HStack w="full" spacing="16px">
            <Flex flex="1" align="center" justify="center">
              <Text
                color="#333333"
                fontWeight="700"
                fontSize="16px"
                lineHeight="24px"
                cursor="pointer"
                onClick={createCalendar}
              >
                {t("Success_add_calender")}
              </Text>
            </Flex>
            <Flex
              align="center"
              justify="center"
              flex="1"
              bg="#333333"
              borderRadius="8px"
              cursor="pointer"
              _hover={{
                bg: "rgba(51, 51, 51, 0.8)",
              }}
              onClick={() => {
                if (isVerifyComplete) {
                  router.push({
                    pathname: "/account/rental_orders",
                    query: router.query,
                  });
                } else {
                  router.push({
                    pathname: "/account/settings",
                    query: router.query,
                  });
                }
              }}
            >
              <Text
                py="16px"
                px="32px"
                color="white"
                fontSize="16px"
                fontWeight="700"
                lineHeight="24px"
              >
                {isVerifyComplete
                  ? t("Success_view_order")
                  : t("Success_go_verify")}
              </Text>
            </Flex>
          </HStack>

          {/* 富邦保險提醒 */}
          <Box mt="24px" pt="24px">
            <Text
              textAlign="center"
              fontSize={{ base: "14px", md: "16px" }}
              lineHeight={{ base: "21px", md: "24px" }}
              color="#748598"
            >
              {t("rent_success_go_insurance_1")}
            </Text>
            <Text
              textAlign="center"
              fontSize={{ base: "14px", md: "16px" }}
              lineHeight={{ base: "21px", md: "24px" }}
              color="#748598"
              cursor="pointer"
              onClick={() => {
                window.open("https://lihi2.com/pyUpK", "_blank");
              }}
            >
              {t("rent_success_go_insurance_2")}
            </Text>
          </Box>
        </VStack>
      </Flex>
    </Flex>
  );
}

export async function getServerSideProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["common"])),
    },
  };
}
export default Success;
